import React, {useMemo} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllkioskDepartmentQuery, useLazyGetAllDepartmentServiceByIdQuery } from "@/services/SelfServiceKiosk";
import { Card, CardContent } from "@/components/ui/Card";
import { Department as KisokDepartment } from "@/app/types/department";
import { requestNotificationPermission } from "@/utils/lib/Fcm";
import { FormProvider, useForm } from "react-hook-form";
import { default as defaultImg } from "@/assets/default.jpg";
import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { Button } from "@/components/ui/Button";
import { ChevronLeftIcon, Search, TicketIcon } from "lucide-react";
import { ChevronRightIcon } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { debounce } from "lodash";
import { TicketStatus, useGetAllListingOfTicketsQuery } from "@/services/LiveQueueService";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { Badge } from "@/components/ui/Badge";
import { displayValue, formatTime, getUserStatusColor, statusLabels } from "@/utils/Helper";
import { useToast } from "@/components/ui/UseToast";
import { messaging } from "@/utils/lib/Firebase";
import { Messaging, onMessage } from "firebase/messaging";
import TicketForm, { FormValues } from "@/components/remote-user/user-portal/components/TicketForm";

const SkeletonCard: React.FC = () => (
  <div className="animate-pulse group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED]">
    <div className="w-full h-[150px] bg-gray-300 rounded-md"></div>
    <div className="mt-4 w-3/4 h-4 bg-gray-300 rounded-md"></div>
  </div>
);

const initialValue = {
  limit: 8,
  offset: 0,
  department_name:""
}
const {
  VITE_IS_PRODUCTION,
  VITE_IS_LOCAL,
  VITE_LOCAL_URL,
  VITE_VIEW_S3_URL
} = import.meta.env;

const kioskDepartmentListing = () => {
  const { toast } = useToast();
  const params = useParams();
  console.log("Params", params)
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const navigate = useNavigate();
  const { searchCriteria, query, setSearchCriteria } =
    useTableData(initialValue);
  const form = useForm<FormValues>({
    defaultValues: {
      title: "",
      description: "",
      date: "",
      time: "",
      service_tag: "",
    }
  });

  const [searchTerm, setSearchTerm] = React.useState("");
  const [departmentId, setDepartmentId] = React.useState<number | undefined>(undefined);

  const queryParams = useMemo(() => {
    const today = new Date().toLocaleDateString("en-CA");

    const params = new URLSearchParams({
      date_from: today,
      date_to: today,
      user_id: userInfo?.id?.toString() ?? "0",
      sort_by: "created_at",
      sort_order: "desc",
      limit: "10",
      offset: "0",
    });

    [TicketStatus.NEW, TicketStatus.CHECKED_IN].forEach((status) => {
      params.append("status", status);
    });

    return params;
  }, [userInfo?.id]);

  const {
    data: departmentData,
    isLoading: isDepartmentDataLoading,
    refetch,
  } = useGetAllListingOfTicketsQuery({ query: queryParams.toString() });
  const {
    data: kioskData,
    isLoading,
    error,
  } = useGetAllkioskDepartmentQuery({
    query: `${query}&domain=${VITE_IS_LOCAL === "true"
      ? VITE_LOCAL_URL
      : VITE_IS_PRODUCTION === "true"
        ? window.location.hostname
        : window.location.host}${params.eventId ? '&event_id=' + params.eventId.toString() : ''}`,
  });

  const totalRecords = kioskData?.total_count ?? 0;
  const { handleNextPage, handlePrevPage, isLastPage } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  React.useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm]);

  React.useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    messaging().then((msgInstance: Messaging | null) => {
      if (!msgInstance) {
        console.error("Firebase Messaging is not initialized.");
        return;
      }

      unsubscribe = onMessage(msgInstance, (payload) => {
        if (payload.notification) {
          toast({
            variant: "default",
            description: payload.notification.body,
            title: payload.notification.title,
          });
          if (payload.notification.title === "Ticket Generated") {
            refetch();
          }
          refetch();
        }
        const { data } = payload;
        if (data?.type && data?.id) {
          
            // `Fetching updated data for type=${data.type}, id=${data.id}`
        }
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    requestNotificationPermission();
  }, []);

  const onClose = () => {
    setDepartmentId(undefined);
    form.reset({
      title: "",
      description: "", // Optional field
      date: "",
      time: "",
      service_tag: ""
    });
  }

  const renderNoDepartmentMessage = () => (
    <div className="text-center py-6 flex justify-center items-center">
      <h2 className="text-gray-600 text-[1.2rem] font-semibold">
        {searchTerm
          ? "No department matches your search."
          : "No department available."}
      </h2>
    </div>
  );

  const handleSearch = React.useCallback(
    debounce((term: string) => {
      setSearchCriteria((prev) => ({ ...prev, department_name: term }));
    }, 500),
    []
  );

  return (
    <div className="w-full">
      {departmentData?.data.length ? (
        <div className="flex flex-row justify-between">
          {" "}
          <div className="px-2 pb-2 text-xl flex font-bold text-primary">
            <div className="text-black">Today's Ticket</div>
          </div>
          <div className="px-2 pb-2 text-xl flex font-bold text-primary flex-row justify-center items-center gap-2">
            {/* <Button
              variant="ghost"
              size="icon"
              className="bg-secondary p-0 rounded-full"
            >
              <Ticket className="h-5 w-5 text-primary" />
            </Button> */}
            <span
              onClick={() => navigate("/user/ticket-listing")}
              className="text-primary underline cursor-pointer"
            >
              View All
            </span>
          </div>
        </div>
      ) : (
        ""
      )}
      <div className="grid grid-cols-1 px-2 gap-2 md:grid-cols-2 w-full">
        {departmentData?.data
          .filter(
            (ticket) =>
              !ticket.ticket_number.toLowerCase().includes("r") ||
              (!ticket.ticket_number.toLowerCase().includes("R") &&
                ticket.status !== TicketStatus.COMPLETED)
          )
          .map((ticket) => (
            <div className="w-full">
              <div className=" overflow-x-auto fancy-scrollbar">
                <button
                  onClick={() => {
                    navigate(
                      `/user/userticketdetail/${ticket.id}/${ticket.ticket_number}/${ticket.service.department_id}/${ticket.service.name}`,
                      {
                        state: {
                          from: "/user/home",
                        },
                      }
                    );
                  }}
                  key={ticket.id}
                  className="p-4 bg-[#FDF8EE] rounded-xl border-2 border-primary flex flex-row sm:flex-row items-center justify-between w-full"
                >
                  {/* Icon Column */}
                  <div className="flex items-center justify-center p-2 bg-secondary border-2 rounded-full">
                    <TicketIcon className="w-6 h-6 text-primary" />
                  </div>

                  {/* Ticket Info Column */}
                  <div className="flex flex-col items-start">
                    <div className="font-semibold md:text-base text-xs text-nowrap">
                      Ticket No: {ticket.ticket_number}
                    </div>

                    <div className="text-[#B5A283] md:text-base text-xs text-nowrap">
                      Counter No - {ticket.counter_id}
                    </div>
                    <div className="text-[#B5A283] md:text-base text-xs text-nowrap items-end flex ">
                      {new Date(`${ticket.date ?? ""}`)
                        .toLocaleDateString("en-PK", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                          .replace(/\//g, "-")
                        }{" "}
                      {displayValue(formatTime(ticket.time))}{" "}
                    </div>
                  </div>
                  {/* Status & Date Column */}
                  <div className="flex flex-col items-center sm:items-end">
                    <Badge
                      className={`capitalize ${getUserStatusColor(ticket.status)} text-white smm:!text-xs`}
                    >
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>
                </button>
              </div>
            </div>
          ))}
      </div>
      <div className="relative w-full px-2 pt-4">
        <Input
          type="text"
          placeholder="Search"
          className="mb-4  pl-10 w-[100%] md:w-[45%]"
          value={searchTerm}
          Icon={
            <Search className="absolute left-3 top-1/2  z-20 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          }
          // onChange={(e) =>
          //    setSearchTerm(e.target.value.trim())}
          onChange={(e) => {
            let value = e.target.value;

            // Remove leading spaces
            value = value.replace(/^\s+/, "");

            // Replace multiple spaces with a single space
            value = value.replace(/\s{2,}/g, " ");

            // Only update if value is non-empty or contains non-space characters
            if (value === "" || /[^\s]/.test(value)) {
              setSearchTerm(value);
            }
          }}
        />
      </div>

      <div className="flex mx-2 gap-2 justify-between items-center">
        <h1 className="text-lg text-black font-bold pb-2">
          Select Department :
        </h1>

        <div className="flex items-center justify-end gap-4 my-4 ">
          {searchCriteria.offset !== 0 && (
            <Button
              variant="solid"
              size="icon"
              onClick={handlePrevPage}
              disabled={searchCriteria.offset === 0}
              className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-7 w-7" />
            </Button>
          )}
          <Button
            variant="solid"
            size="icon"
            onClick={handleNextPage}
            disabled={isLastPage}
            className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-7 w-7" />
          </Button>
        </div>
      </div>
      <div className="overflow-hidden p-2">
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
          <div
            className={
              !error && (kioskData?.data?.length ?? 0) > 0
                ? "mx-4 grid gap-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-4 my-0 md:mt-8"
                : ""
            }
          >
            {(() => {
              if (error) {
                // Handle error state
                return (
                  <div className="text-center py-6 w-full">
                    <h2 className="text-gray-400 text-[1.2rem] w-full font-semibold">
                      {
                        // Check if error is FetchBaseQueryError and has status property
                        error && "status" in error
                          ? error.status === 500
                            ? "Unable to load departments. Please check your connection and try again."
                            : error.status === 404
                              ? searchTerm.length
                                ? "No departments match your search criteria."
                                : "No departments available at the moment."
                              : "Failed to fetch departments. Please try again."
                          : // Handle SerializedError (generic error case)
                            "An unknown error occurred. Please try again."
                      }
                    </h2>
                  </div>
                );
              } else if (isLoading) {
                return Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={Number(index)} />
                ));
              } else if ((kioskData?.data?.length ?? 0) > 0) {
                return kioskData?.data.map((item, index) => (
                  <Card
                    key={Number(index)}
                    onClick={() => setDepartmentId(item.id)}
                    className="group relative flex transition-all hover:shadow-lg flex-col items-center justify-center w-full py-4 bg-secondary hover:cursor-pointer p-2"
                  >
                    <CardContent className="flex flex-col items-center justify-center text-center leading-0  p-0 m-0">
                      <div className="w-[100%] h-[100%] justify-center items-center flex">
                        <img
                          src={
                            item.logo
                              ? `${VITE_VIEW_S3_URL + item.logo}`
                              : defaultImg
                          }
                          alt="Company logo"
                          className="w-[150px] h-[150px] object-contain"
                        />
                      </div>
                      <h3 className="capitalize text-center text-xs md:text-lg font-semibold text-black">
                        {item.name}
                      </h3>
                    </CardContent>
                  </Card>
                ));
              } else {
                return renderNoDepartmentMessage();
              }
            })()}

            <FormProvider {...form}>
              <TicketForm 
                departmentId={departmentId}
                open={!!departmentId}
                onClose={onClose}
              />
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default kioskDepartmentListing;
